// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useState } from "react";
import { Button, Empty, Input, Modal, Tag, Tooltip, Typography } from "antd";
import { AppstoreOutlined, NumberOutlined, PlusOutlined, LoginOutlined, LogoutOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import type { SpaceChildView } from "./Workspace";

// SpaceOverview is what a Space shows when selected — a community landing page,
// NOT a chat timeline (a Space is a container, so messaging into it is
// meaningless). It surfaces the space's description and its channels: joined ones
// open, not-yet-joined ones join in place.
export function SpaceOverview({
  name,
  topic,
  members,
  channels,
  isOp,
  onOpenChild,
  onCreateChannel,
  onLeave,
  onToggleSuggested,
}: {
  readonly name: string;
  readonly topic: string;
  readonly members: number;
  readonly channels: SpaceChildView[];
  readonly isOp: boolean;
  readonly onOpenChild: (child: SpaceChildView) => void;
  readonly onCreateChannel: (channelName: string) => void;
  readonly onLeave: () => void;
  readonly onToggleSuggested: (childID: string, suggested: boolean) => void;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [value, setValue] = useState("");

  function confirmCreate() {
    const n = value.trim();
    if (n) onCreateChannel(n);
    setValue("");
    setCreateOpen(false);
  }

  return (
    <div className="space-overview">
      <div className="space-hero">
        <AppstoreOutlined className="space-hero-icon" />
        <div className="space-hero-text">
          <Typography.Title level={3} style={{ margin: 0 }}>
            {name}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ margin: "4px 0 0" }}>
            {topic || "A space — a community of channels."}
          </Typography.Paragraph>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {members} {members === 1 ? "member" : "members"}
          </Typography.Text>
        </div>
        <Button danger size="small" icon={<LogoutOutlined />} className="space-leave-btn" onClick={onLeave}>
          Leave space
        </Button>
      </div>

      <div className="space-section-head">
        <Typography.Text strong>Channels ({channels.length})</Typography.Text>
        {isOp && (
          <Button size="small" icon={<PlusOutlined />} onClick={() => { setValue(""); setCreateOpen(true); }}>
            New channel
          </Button>
        )}
      </div>

      {channels.length === 0 ? (
        <Empty description="No channels in this space yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className="space-channel-grid">
          {channels.map((c) => (
            <div key={c.id} className="space-channel-card">
              <button type="button" className="space-channel-open" onClick={() => onOpenChild(c)}>
                <NumberOutlined className="space-channel-icon" />
                <span className="space-channel-name">{c.name}</span>
                {c.suggested && <Tag color="blue">suggested</Tag>}
                {c.room ? <Tag color="green">open</Tag> : <Tag icon={<LoginOutlined />}>join</Tag>}
              </button>
              {isOp && (
                <Tooltip title={c.suggested ? "Remove suggestion" : "Suggest to new members"}>
                  <Button
                    type="text"
                    size="small"
                    className="space-channel-suggest"
                    icon={c.suggested ? <StarFilled style={{ color: "#1677ff" }} /> : <StarOutlined />}
                    onClick={() => onToggleSuggested(c.id, !c.suggested)}
                  />
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal title={`New channel in ${name}`} open={createOpen} onOk={confirmCreate} onCancel={() => setCreateOpen(false)} okText="Create">
        <Input placeholder="channel name" value={value} onChange={(e) => setValue(e.target.value)} onPressEnter={confirmCreate} autoFocus />
      </Modal>
    </div>
  );
}
